// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — Barber Growth System Brain (2026-08-22)
 * ==============================================
 * ONE source of truth for the AUTO-COMPUTED business/development score that drives incentives,
 * the staged Growth Track, bonus eligibility, and the price-increase trigger. Pure logic — NO
 * React, NO Firebase (mirrors ppi.ts). The service layer aggregates raw per-barber metrics and
 * calls this; every surface (profile, coaching, goals, AI manager) reads the SAME result so
 * numbers can never drift between screens.
 *
 * Relationship to PPI (shared/ppi.ts): PPI = the coach-entered QUALITY/TRUST instrument (feeds the
 * emergency gate). Growth Score = the auto-computed BUSINESS/DEVELOPMENT engine. They are
 * complementary (like client CPI + tier). Growth Score's components also auto-fill PPI's `business`
 * pillar — see feedBusinessPillar() — so the coaching chain gets real data instead of manual entry.
 *
 * DESIGN (Jose, 2026-08-22): "fix the formula, configure the parameters." The MATH here is constant;
 * every number (weights, targets, tenure windows, bonus %, gates) lives in GrowthConfig and is
 * owner-tunable per shop. Principles: normalize every metric to its target (fair across tenure/scale);
 * bonuses = % of value created (self-funding); score on a rolling window (anti-gaming).
 *
 * STAGED GROWTH TRACK: metrics roll in as a barber matures (easiest-to-control first, hardest-to-
 * mature last), the occupancy target rises, and weights renormalize over active metrics — so being
 * excellent gets harder as they become more capable/better-paid.
 *   S1 Foundation (Occupancy + Rating)          occ target 55%
 *   S2 Loyalty    (+ Returning)                 occ target 65%
 *   S3 Value      (+ Retail)                    occ target 75%
 *   S4 Full Barber(+ Rebooking → all 5)         occ target 85%
 *
 * "MASTER IT, OR GRIND IT" advancement: DEFAULT = mastery (hit the stage's occupancy target on the
 * rolling window → graduate immediately). Backstop = a longer time window that advances PROVISIONAL
 * (bonus capped a tier, no Gold) until the skipped mark is hit. Performance-adjusted: trending up near
 * target → early nudge; flat/declining → coaching flag instead of a silent pass (handled by service).
 *
 * THREE FAIRNESS FIXES: (1) rebooking uses a ROLLING MATURE WINDOW (service passes it; don't use
 * YTD-to-today); (2) rating is NEUTRAL (dropped + reweighted) when reviewCount < minReviews;
 * (3) RAMP GRACE — new barbers are only scored on their active-stage metrics.
 *
 * DO NOT EDIT the per-app copies in <app>/src/shared/ — edit THIS file at repo root and run
 * scripts/sync-shared.sh to propagate.
 */

// ── Component keys ────────────────────────────────────────────────────────────────────
export type GrowthMetricKey = 'occupancy' | 'rebooking' | 'returning' | 'retail' | 'rating';

// ── The staged Growth Track ───────────────────────────────────────────────────────────
export interface GrowthStage {
  stage: 1 | 2 | 3 | 4;
  name: string;
  /** Metrics that are LIVE (scored) at this stage. Weights renormalize over these. */
  active: GrowthMetricKey[];
}

export const GROWTH_STAGES: GrowthStage[] = [
  { stage: 1, name: 'Foundation', active: ['occupancy', 'rating'] },
  { stage: 2, name: 'Loyalty', active: ['occupancy', 'rating', 'returning'] },
  { stage: 3, name: 'Value', active: ['occupancy', 'rating', 'returning', 'retail'] },
  { stage: 4, name: 'Full Barber', active: ['occupancy', 'rating', 'returning', 'retail', 'rebooking'] },
];

export type GrowthTier = 'none' | 'bronze' | 'silver' | 'gold';

// ── Config — every tunable parameter (owner-settable per shop) ────────────────────────
export interface GrowthConfig {
  /** Balanced weights at Stage 4 (sum to 100). Renormalized over active metrics in earlier stages. */
  weights: Record<GrowthMetricKey, number>;
  /** "Full marks" targets. occupancy target is overridden per-stage by occupancyTargetRamp. */
  targets: {
    occupancy: number;   // % (stage-4 / full standard; earlier stages use the ramp)
    rebooking: number;   // %
    returning: number;   // %
    retailPct: number;   // retail $ as % of service revenue
    rating: number;      // avg rating (denominator, e.g. 4.9)
  };
  /** Tenure weeks spent in each of stages 1-3 before the next metric unlocks (time path). */
  stageWindows: [number, number, number];
  /** Occupancy "full marks" bar per stage (index 0 = stage 1). */
  occupancyTargetRamp: [number, number, number, number];
  /** Default advancement mode. 'mastery' = graduate on hitting the stage target; 'time' = calendar;
   *  'hybrid' = whichever first. Jose's default = 'mastery' with the provisional time backstop. */
  advancement: 'mastery' | 'time' | 'hybrid';
  /** Grind-path backstop = stageWindow * this multiplier before a non-mastered barber advances provisional. */
  backstopMultiplier: number;
  /** "close enough to nudge early" — within this many points of target + trending up → early graduate. */
  earlyNudgePoints: number;
  /** Rating is dropped (neutral) below this review count. */
  minReviewsForRating: number;
  /** Minimum scheduled available hours/week to qualify for Silver/Gold (anti-gaming hour-cutters). */
  minHoursForTopTiers: number;
  /** Tier score thresholds (0-100). */
  tiers: { bronzeMin: number; silverMin: number; goldMin: number };
  /** Bonus = this % of the period's service revenue, by tier. */
  tierBonusPct: { bronze: number; silver: number; gold: number };
  /** Whether the bonus % stacks above the 50% commission cap for maxed barbers (Jose: yes). */
  bonusStacksAboveCap: boolean;
  /** Consecutive Gold periods (at full occupancy) required to flag a price increase. */
  priceIncreasePeriods: number;
  /** Pay-week start day: 0=Sun … 6=Sat. The commission sliding scale resets each pay week. Default Wed (3). */
  payWeekStartDay: number;
}

export const DEFAULT_GROWTH_CONFIG: GrowthConfig = {
  weights: { occupancy: 35, rebooking: 20, returning: 20, retail: 15, rating: 10 },
  targets: { occupancy: 85, rebooking: 50, returning: 70, retailPct: 5, rating: 4.9 },
  stageWindows: [4, 4, 4],
  occupancyTargetRamp: [55, 65, 75, 85],
  advancement: 'mastery',
  backstopMultiplier: 2,
  earlyNudgePoints: 5,
  minReviewsForRating: 3,
  minHoursForTopTiers: 25,
  tiers: { bronzeMin: 55, silverMin: 75, goldMin: 90 },
  tierBonusPct: { bronze: 1, silver: 2, gold: 3 },
  bonusStacksAboveCap: true,
  priceIncreasePeriods: 3,
  payWeekStartDay: 3, // Wednesday (Wed–Tue pay week)
};

/** Fill any missing field with the default; deep-merges the nested groups so an owner-saved partial
 *  (or an older shape) loads safely. Pure. Mirror of mergeCommissionPay. The Settings "Growth & Bonus"
 *  editor persists via this, and recomputeAllFromLiveData reads it. (Jose 2026-09-27) */
export function mergeGrowthConfig(p?: Partial<GrowthConfig> | null): GrowthConfig {
  const d = DEFAULT_GROWTH_CONFIG;
  const n = (v: any, dflt: number) => (typeof v === 'number' && isFinite(v) ? v : dflt);
  return {
    weights: {
      occupancy: n(p?.weights?.occupancy, d.weights.occupancy),
      rebooking: n(p?.weights?.rebooking, d.weights.rebooking),
      returning: n(p?.weights?.returning, d.weights.returning),
      retail: n(p?.weights?.retail, d.weights.retail),
      rating: n(p?.weights?.rating, d.weights.rating),
    },
    targets: {
      occupancy: n(p?.targets?.occupancy, d.targets.occupancy),
      rebooking: n(p?.targets?.rebooking, d.targets.rebooking),
      returning: n(p?.targets?.returning, d.targets.returning),
      retailPct: n(p?.targets?.retailPct, d.targets.retailPct),
      rating: n(p?.targets?.rating, d.targets.rating),
    },
    stageWindows: (Array.isArray(p?.stageWindows) && p!.stageWindows.length === 3 ? p!.stageWindows : d.stageWindows) as [number, number, number],
    occupancyTargetRamp: (Array.isArray(p?.occupancyTargetRamp) && p!.occupancyTargetRamp.length === 4 ? p!.occupancyTargetRamp : d.occupancyTargetRamp) as [number, number, number, number],
    advancement: p?.advancement === 'time' || p?.advancement === 'hybrid' ? p.advancement : d.advancement,
    backstopMultiplier: n(p?.backstopMultiplier, d.backstopMultiplier),
    earlyNudgePoints: n(p?.earlyNudgePoints, d.earlyNudgePoints),
    minReviewsForRating: n(p?.minReviewsForRating, d.minReviewsForRating),
    minHoursForTopTiers: n(p?.minHoursForTopTiers, d.minHoursForTopTiers),
    tiers: {
      bronzeMin: n(p?.tiers?.bronzeMin, d.tiers.bronzeMin),
      silverMin: n(p?.tiers?.silverMin, d.tiers.silverMin),
      goldMin: n(p?.tiers?.goldMin, d.tiers.goldMin),
    },
    tierBonusPct: {
      bronze: n(p?.tierBonusPct?.bronze, d.tierBonusPct.bronze),
      silver: n(p?.tierBonusPct?.silver, d.tierBonusPct.silver),
      gold: n(p?.tierBonusPct?.gold, d.tierBonusPct.gold),
    },
    bonusStacksAboveCap: typeof p?.bonusStacksAboveCap === 'boolean' ? p.bonusStacksAboveCap : d.bonusStacksAboveCap,
    priceIncreasePeriods: n(p?.priceIncreasePeriods, d.priceIncreasePeriods),
    payWeekStartDay: n(p?.payWeekStartDay, d.payWeekStartDay),
  };
}

const METRIC_LABELS: Record<GrowthMetricKey, string> = {
  occupancy: 'Occupancy', rebooking: 'Rebooking', returning: 'Returning clients',
  retail: 'Retail', rating: 'Rating',
};

// ── Inputs — aggregated per-barber metrics for the period (service layer computes these) ─
export interface GrowthInputs {
  occupancyPct: number;          // 0-100 booked ÷ available for the (rolling) period
  rebookingPct: number;          // 0-100 on the ROLLING MATURE WINDOW (service ensures maturity)
  returningPct: number;          // 0-100 clients returning to THIS barber (same barberId)
  retailPct: number;             // retail $ as % of service revenue (0-100+)
  rating: number;                // avg rating 0-5
  reviewCount: number;           // drives the neutral-rating rule
  availableHoursPerWeek: number; // for the min-hours floor
  tenureWeeks: number;           // weeks since startDate
  weeklyServiceRevenue: number;  // for the bonus $ estimate
  weeklyRetailDollars?: number;  // display-only (retail spiff); engine ignores it
  /** OPTIONAL persisted stage (from prior compute). If omitted, resolved from tenure. */
  stage?: 1 | 2 | 3 | 4;
  /** OPTIONAL: occupancy change vs the prior window (points), for performance-adjusted advancement. */
  occupancyTrend?: number;
  /** OPTIONAL: was this stage reached via the grind (time) backstop without mastery? */
  provisional?: boolean;
}

// ── Output ────────────────────────────────────────────────────────────────────────────
export interface GrowthComponent {
  key: GrowthMetricKey;
  label: string;
  value: number;                 // the raw metric value
  target: number;                // the target it's measured against (stage-adjusted for occupancy)
  componentScore: number;        // 0-100 (value/target capped 100); -1 if inactive/neutral
  weight: number;                // renormalized weight within active metrics (0 if inactive)
  active: boolean;
}

export interface GrowthResult {
  score: number;                 // 0-100 over ACTIVE-stage metrics (renormalized)
  stage: 1 | 2 | 3 | 4;
  stageName: string;
  activeMetrics: GrowthMetricKey[];
  occupancyTargetThisStage: number;
  tier: GrowthTier;
  provisional: boolean;          // advanced via grind path without mastery → bonus capped
  components: GrowthComponent[];
  weakest: GrowthComponent | null;   // lowest active component → coaching focus
  bonus: { eligible: boolean; tierPct: number; estWeekly: number; note: string };
  gates: { minHoursMet: boolean; silverOcc: number; goldOcc: number };
  advancement: {
    mode: GrowthConfig['advancement'];
    mastered: boolean;           // hit this stage's occupancy target
    shouldPromote: boolean;      // recommend moving to next stage now
    reason: string;
  };
  priceIncreaseEligibleThisPeriod: boolean;  // this period qualifies (sustained check needs history — service)
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const round = (n: number) => Math.round(n);

/** Resolve which stage a barber is in from tenure alone (time path) — the floor if no mastery recorded. */
export function resolveStageFromTenure(tenureWeeks: number, config: GrowthConfig = DEFAULT_GROWTH_CONFIG): 1 | 2 | 3 | 4 {
  const [w1, w2, w3] = config.stageWindows;
  if (tenureWeeks < w1) return 1;
  if (tenureWeeks < w1 + w2) return 2;
  if (tenureWeeks < w1 + w2 + w3) return 3;
  return 4;
}

/** The occupancy "full marks" target for a given stage. */
export function occupancyTargetForStage(stage: 1 | 2 | 3 | 4, config: GrowthConfig = DEFAULT_GROWTH_CONFIG): number {
  return config.occupancyTargetRamp[stage - 1];
}

/** Stage-relative tier occupancy gates: gold gate = this stage's occ target; silver = target − 10. */
function occGatesForStage(stage: 1 | 2 | 3 | 4, config: GrowthConfig): { silverOcc: number; goldOcc: number } {
  const goldOcc = occupancyTargetForStage(stage, config);
  return { silverOcc: Math.max(0, goldOcc - 10), goldOcc };
}

/**
 * THE core compute. Given a barber's aggregated metrics + config, returns the full Growth result:
 * staged score (renormalized over active metrics), tier (occupancy-gated), bonus, and an advancement
 * recommendation. Pure + deterministic → unit-testable.
 */
export function computeGrowth(inputs: GrowthInputs, config: GrowthConfig = DEFAULT_GROWTH_CONFIG): GrowthResult {
  const stage = inputs.stage ?? resolveStageFromTenure(inputs.tenureWeeks, config);
  const stageDef = GROWTH_STAGES[stage - 1];
  const occTarget = occupancyTargetForStage(stage, config);

  // Rating neutrality: below the review threshold, rating is dropped from the active set.
  const ratingActive = inputs.reviewCount >= config.minReviewsForRating;
  const active = stageDef.active.filter(k => !(k === 'rating' && !ratingActive));

  // Raw value + target per metric (occupancy target is stage-adjusted).
  const rawValue = (k: GrowthMetricKey): number => {
    switch (k) {
      case 'occupancy': return inputs.occupancyPct;
      case 'rebooking': return inputs.rebookingPct;
      case 'returning': return inputs.returningPct;
      case 'retail': return inputs.retailPct;
      case 'rating': return inputs.rating;
    }
  };
  const targetFor = (k: GrowthMetricKey): number => {
    switch (k) {
      case 'occupancy': return occTarget;
      case 'rebooking': return config.targets.rebooking;
      case 'returning': return config.targets.returning;
      case 'retail': return config.targets.retailPct;
      case 'rating': return config.targets.rating;
    }
  };

  // Renormalize the Balanced weights over the active metrics so the score stays 0-100.
  const activeWeightSum = active.reduce((s, k) => s + config.weights[k], 0) || 1;

  const components: GrowthComponent[] = (Object.keys(config.weights) as GrowthMetricKey[]).map(k => {
    const isActive = active.includes(k);
    const value = rawValue(k);
    const target = targetFor(k);
    const componentScore = isActive ? clamp((value / (target || 1)) * 100, 0, 100) : -1;
    const weight = isActive ? (config.weights[k] / activeWeightSum) * 100 : 0;
    return { key: k, label: METRIC_LABELS[k], value, target, componentScore: isActive ? round(componentScore) : -1, weight: round(weight), active: isActive };
  });

  const score = round(
    components.filter(c => c.active).reduce((s, c) => s + c.componentScore * (c.weight / 100), 0)
  );

  // Weakest active component = the coaching focus.
  const activeComps = components.filter(c => c.active);
  const weakest = activeComps.length ? activeComps.reduce((min, c) => (c.componentScore < min.componentScore ? c : min), activeComps[0]) : null;

  // Gates.
  const minHoursMet = inputs.availableHoursPerWeek >= config.minHoursForTopTiers;
  const { silverOcc, goldOcc } = occGatesForStage(stage, config);

  // Tier: score band + occupancy gate + min-hours floor for the top tiers.
  let tier: GrowthTier = 'none';
  if (score >= config.tiers.bronzeMin) tier = 'bronze';
  if (score >= config.tiers.silverMin && inputs.occupancyPct >= silverOcc && minHoursMet) tier = 'silver';
  if (score >= config.tiers.goldMin && inputs.occupancyPct >= goldOcc && minHoursMet) tier = 'gold';
  // If score qualifies for a top tier but a gate fails, hold at the highest allowed lower tier.
  if (score >= config.tiers.silverMin && tier === 'bronze') { /* held at bronze by gate */ }

  // Provisional (grind-path) caps the bonus one tier down and blocks Gold.
  const provisional = !!inputs.provisional;
  let effectiveTier = tier;
  if (provisional) {
    if (tier === 'gold') effectiveTier = 'silver';
    else if (tier === 'silver') effectiveTier = 'bronze';
  }

  const tierPct = effectiveTier === 'gold' ? config.tierBonusPct.gold
    : effectiveTier === 'silver' ? config.tierBonusPct.silver
    : effectiveTier === 'bronze' ? config.tierBonusPct.bronze : 0;
  const estWeekly = round((tierPct / 100) * inputs.weeklyServiceRevenue * 100) / 100;
  const bonusNote = provisional && tier !== effectiveTier
    ? `Provisional (advanced without mastery) — bonus capped at ${effectiveTier}. Hit occupancy ${occTarget}% to earn full ${tier}.`
    : effectiveTier === 'none' ? 'Below Bronze — still building.' : `${effectiveTier} → ${tierPct}% of services.`;

  // Advancement recommendation ("master it, or grind it").
  const mastered = inputs.occupancyPct >= occTarget;
  const cumWindow = (() => { const w = config.stageWindows; let s = 0; for (let i = 0; i < stage; i++) s += w[i] ?? 0; return s; })();
  const backstopReached = inputs.tenureWeeks >= cumWindow * config.backstopMultiplier;
  const trendingUpNearTarget = (inputs.occupancyTrend ?? 0) > 0 && inputs.occupancyPct >= occTarget - config.earlyNudgePoints;
  let shouldPromote = false, reason = '';
  if (stage >= 4) { reason = 'Full Barber — all metrics live.'; }
  else if (mastered) { shouldPromote = true; reason = `Mastered stage ${stage} (occupancy ${round(inputs.occupancyPct)}% ≥ ${occTarget}%) → promote clean.`; }
  else if (config.advancement !== 'time' && trendingUpNearTarget) { shouldPromote = true; reason = `Trending up and within ${config.earlyNudgePoints} pts of target → early nudge promote.`; }
  else if (config.advancement !== 'mastery' && inputs.tenureWeeks >= cumWindow) { shouldPromote = true; reason = 'Time window reached (hybrid/time mode) → promote.'; }
  else if (backstopReached) { shouldPromote = true; reason = `Grind backstop reached (${round(inputs.tenureWeeks)}w) → promote PROVISIONAL until occupancy ${occTarget}% is hit.`; }
  else { reason = `Building toward stage ${stage} target (occupancy ${round(inputs.occupancyPct)}% / ${occTarget}%).`; }

  const priceIncreaseEligibleThisPeriod = tier === 'gold' && inputs.occupancyPct >= config.targets.occupancy && !provisional;

  return {
    score, stage, stageName: stageDef.name, activeMetrics: active, occupancyTargetThisStage: occTarget,
    tier, provisional, components, weakest,
    bonus: { eligible: effectiveTier !== 'none', tierPct, estWeekly, note: bonusNote },
    gates: { minHoursMet, silverOcc, goldOcc },
    advancement: { mode: config.advancement, mastered, shouldPromote, reason },
    priceIncreaseEligibleThisPeriod,
  };
}

// ── Projection (the "book at 80/85%" calculator) ──────────────────────────────────────
/** Revenue per booked hour = service revenue ÷ booked hours (a barber's ~stable productivity). */
export function revenuePerBookedHour(serviceRevenue: number, bookedHours: number): number {
  return bookedHours > 0 ? serviceRevenue / bookedHours : 0;
}

export interface GrowthProjection { weekly: number; monthly: number; annual: number; }

/** Projected services at a target occupancy, holding available hours + $/booked-hr constant. */
export function projectAtOccupancy(
  availableHoursPerWeek: number,
  revPerBookedHour: number,
  targetOccupancyPct: number,
): GrowthProjection {
  const bookedPerWeek = availableHoursPerWeek * (targetOccupancyPct / 100);
  const weekly = bookedPerWeek * revPerBookedHour;
  return { weekly: round(weekly), monthly: round(weekly * 4.333), annual: round(weekly * 52) };
}

// ── Commission sliding scale (weekly Wed–Tue, CLIFF: whole-check at the tier hit) ──────
export interface CommissionTier { min: number; rate: number; }
export const DEFAULT_COMMISSION_TIERS: CommissionTier[] = [
  { min: 0, rate: 0.40 },
  { min: 2000, rate: 0.45 },
  { min: 3500, rate: 0.50 },
];

/** Per-shop "Commission & Pay" config (Settings-editable, the single source of truth for barber pay).
 *  Weekly sliding-scale cliff, resets each pay week. rate is a DECIMAL (0.45 = 45%). */
export interface CommissionPayConfig {
  payWeekStartDay: number;   // 0=Sun … 6=Sat (default Wed)
  tiers: CommissionTier[];   // {min $, rate decimal}
  retailSpiffPct: number;    // % of retail sales paid as spiff (e.g. 17.5)
  /** 'YYYY-MM-DD' — reports treat this date as the switch from Fresha history to live SLIK data.
   *  null = not set → Fresha history stays authoritative for all reporting.
   *  (Restored 2026-09-20 from the babel cache after an unsynced src/shared edit was clobbered —
   *  edit THIS root file only, then scripts/sync-shared.sh. Consumers: CommissionPayEditor,
   *  BarberFinancialSummary live-blend.) */
  cutoverDate: string | null;
}
export const DEFAULT_COMMISSION_PAY: CommissionPayConfig = {
  payWeekStartDay: 3,        // Wednesday
  tiers: DEFAULT_COMMISSION_TIERS,
  retailSpiffPct: 17.5,
  cutoverDate: null,
};
/** Fill any missing fields with defaults; sort tiers ascending. Pure. */
export function mergeCommissionPay(p?: Partial<CommissionPayConfig> | null): CommissionPayConfig {
  return {
    payWeekStartDay: typeof p?.payWeekStartDay === 'number' ? p.payWeekStartDay : DEFAULT_COMMISSION_PAY.payWeekStartDay,
    tiers: p?.tiers && p.tiers.length ? [...p.tiers].sort((a, b) => a.min - b.min) : DEFAULT_COMMISSION_PAY.tiers,
    retailSpiffPct: typeof p?.retailSpiffPct === 'number' ? p.retailSpiffPct : DEFAULT_COMMISSION_PAY.retailSpiffPct,
    cutoverDate: p?.cutoverDate ?? DEFAULT_COMMISSION_PAY.cutoverDate,
  };
}

/** Cliff model: the whole period's revenue earns the rate of the highest tier crossed. */
export function commissionForRevenue(
  weeklyServiceRevenue: number,
  tiers: CommissionTier[] = DEFAULT_COMMISSION_TIERS,
): { rate: number; commission: number; nextTierAt: number | null; toNextTier: number | null } {
  const sorted = [...tiers].sort((a, b) => a.min - b.min);
  let rate = sorted[0]?.rate ?? 0;
  for (const t of sorted) if (weeklyServiceRevenue >= t.min) rate = t.rate;
  const next = sorted.find(t => t.min > weeklyServiceRevenue) || null;
  return {
    rate,
    commission: round(weeklyServiceRevenue * rate * 100) / 100,
    nextTierAt: next ? next.min : null,
    toNextTier: next ? round((next.min - weeklyServiceRevenue) * 100) / 100 : null,
  };
}

/**
 * THE per-barber commission resolver (Jose 2026-09-01) — the ONE brain every earnings/report surface
 * reads (payroll, daily-perf, ComprehensiveBarberProfile, Growth cards). A barber on `commissionMode:'flat'`
 * earns a fixed `commissionRate`% of service revenue; everyone else (default 'sliding') uses the shop
 * sliding-scale cliff via commissionForRevenue. See project-slik-commission-shared-brain-2026-09-01.
 */
export function commissionForBarber(
  barber: { commissionMode?: 'flat' | 'sliding'; commissionRate?: number } | null | undefined,
  serviceRevenue: number,
  tiers: CommissionTier[] = DEFAULT_COMMISSION_TIERS,
): { rate: number; commission: number; flat: boolean; nextTierAt: number | null; toNextTier: number | null } {
  if (barber?.commissionMode === 'flat') {
    const rate = (barber.commissionRate || 0) / 100;
    return { rate, commission: round(serviceRevenue * rate * 100) / 100, flat: true, nextTierAt: null, toNextTier: null };
  }
  const c = commissionForRevenue(serviceRevenue, tiers);
  return { rate: c.rate, commission: c.commission, flat: false, nextTierAt: c.nextTierAt, toNextTier: c.toNextTier };
}

/** Total weekly take = commission (+ Growth bonus if it stacks above the cap) + retail spiff. */
export function totalWeeklyEarnings(
  weeklyServiceRevenue: number,
  growth: GrowthResult,
  retailSalesDollars: number,
  opts?: { retailSpiffPct?: number; tiers?: CommissionTier[]; config?: GrowthConfig },
): { commission: number; commissionRate: number; growthBonus: number; retailSpiff: number; total: number } {
  const config = opts?.config ?? DEFAULT_GROWTH_CONFIG;
  const retailSpiffPct = opts?.retailSpiffPct ?? 17.5;
  const comm = commissionForRevenue(weeklyServiceRevenue, opts?.tiers);
  const growthBonus = config.bonusStacksAboveCap ? growth.bonus.estWeekly : 0;
  const retailSpiff = round(retailSalesDollars * (retailSpiffPct / 100) * 100) / 100;
  const total = round((comm.commission + growthBonus + retailSpiff) * 100) / 100;
  return { commission: comm.commission, commissionRate: comm.rate, growthBonus, retailSpiff, total };
}

// ── GROWTH HABIT / LADDER (Jose 2026-10-16) ───────────────────────────────────────────────────────
// Turn a %-target into an ACHIEVABLE per-barber daily habit + the pay it earns. "Show the path, not the
// prize": "$9,452 more retail" → "~1.5 products per barber per day → +$Y in your check." Pure + metric-
// agnostic — reused by owner reports and the barber pay-week card. See GROWTH-ENGINE-SPEC.md.

/** Default incremental ladder (percent rungs). Owner can switch a metric to manual rungs. */
export const DEFAULT_GROWTH_LADDER = [2, 4, 6, 8, 10];

/** Smallest rung strictly above currentPct. `mode:'manual'` uses supplied rungs; else the auto ladder. */
export function nextRung(currentPct: number, ladder?: { mode?: 'auto' | 'manual'; rungs?: number[] }): number {
  const rungs = (ladder?.mode === 'manual' && ladder.rungs && ladder.rungs.length)
    ? [...ladder.rungs].sort((a, b) => a - b)
    : DEFAULT_GROWTH_LADDER;
  return rungs.find((r) => r > currentPct + 1e-9) ?? rungs[rungs.length - 1];
}

export interface HabitInput {
  currentPct: number;    // current metric % (e.g. retail attachment 0.5)
  base: number;          // the $ the % rides on for the period (e.g. service revenue)
  unitValue: number;     // avg $ per action (avg product / service price)
  barbers: number;       // barbers sharing the goal (1 for a single-barber card)
  openDays: number;      // shop-open days in the period
  spiffPct?: number;     // the barber's cut per $ of the action (retail spiff, e.g. 17.5) — pay math
  targetPct?: number;    // explicit target; else the next ladder rung
  ladder?: { mode?: 'auto' | 'manual'; rungs?: number[] };
}

export interface HabitPlan {
  currentPct: number;
  targetPct: number;
  gapDollars: number;        // shop $ from current → target
  gapUnits: number;          // # of actions to close it
  perBarberUnits: number;    // actions each barber needs over the period
  perBarberPerDay: number;   // the HABIT
  cadence: string;           // "about 1 per barber per day" | "~1 per barber every 5 days"
  barberPay: number;         // $ each barber earns for their share (via spiffPct)
  shopGain: number;          // $ the shop keeps after the spiff
}

function cadenceLabel(perBarberPerDay: number): string {
  if (perBarberPerDay <= 0) return 'goal already met';
  if (perBarberPerDay >= 0.85) return `about ${Math.round(perBarberPerDay)} per barber per day`;
  return `about 1 per barber every ${Math.max(1, Math.round(1 / perBarberPerDay))} days`;
}

export function habitPlan(input: HabitInput): HabitPlan {
  const barbers = Math.max(1, input.barbers || 1);
  const openDays = Math.max(1, input.openDays || 1);
  const unit = Math.max(0.01, input.unitValue || 0.01);
  const targetPct = input.targetPct ?? nextRung(input.currentPct, input.ladder);
  const gapDollars = Math.max(0, ((targetPct - input.currentPct) / 100) * (input.base || 0));
  const gapUnits = gapDollars / unit;
  const perBarberUnits = gapUnits / barbers;
  const perBarberPerDay = perBarberUnits / openDays;
  const spiff = (input.spiffPct ?? 0) / 100;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  return {
    currentPct: input.currentPct,
    targetPct,
    gapDollars: r2(gapDollars),
    gapUnits: Math.round(gapUnits * 10) / 10,
    perBarberUnits: Math.round(perBarberUnits * 10) / 10,
    perBarberPerDay: r2(perBarberPerDay),
    cadence: cadenceLabel(perBarberPerDay),
    barberPay: r2(perBarberUnits * unit * spiff),
    shopGain: r2(gapDollars - gapUnits * unit * spiff),
  };
}

// ── "This pay week" live cliff projection — the weekly twin of the occupancy projection ──────────────
/** Most recent pay-week start (that weekday at 00:00 local) at/before `now`. startDay: 0=Sun … 6=Sat. */
export function currentPayWeekStart(now: Date, startDay: number): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const back = (d.getDay() - startDay + 7) % 7;
  d.setDate(d.getDate() - back);
  return d;
}

export interface PayWeekProjection {
  currentRevenue: number;         // services booked-to-date THIS pay week
  currentRate: number;            // commission rate at current revenue (e.g. 0.40)
  currentTake: number;            // revenue × rate (commission so far)
  nextTierAt: number | null;      // $ threshold of the next tier
  nextRate: number | null;        // rate once the next tier is crossed
  toNextTier: number | null;      // $ more in services to reach it
  takeAtNextTier: number | null;  // whole-check take the moment they cross (CLIFF)
  checkJump: number | null;       // takeAtNextTier − currentTake (the motivating increase)
}

/** Live cliff projection: where the barber is this pay week + what crossing the next tier does to the
 *  WHOLE check (cliff model). Drives the "you're $X from a bigger check" motivator. */
export function projectPayWeek(currentRevenue: number, tiers: CommissionTier[] = DEFAULT_COMMISSION_TIERS): PayWeekProjection {
  const sorted = [...tiers].sort((a, b) => a.min - b.min);
  const comm = commissionForRevenue(currentRevenue, sorted);
  const currentTake = round(currentRevenue * comm.rate * 100) / 100;
  const next = sorted.find(t => t.min > currentRevenue) || null;
  const takeAtNextTier = next ? round(next.min * next.rate * 100) / 100 : null;
  return {
    currentRevenue: round(currentRevenue * 100) / 100,
    currentRate: comm.rate,
    currentTake,
    nextTierAt: comm.nextTierAt,
    nextRate: next ? next.rate : null,
    toNextTier: comm.toNextTier,
    takeAtNextTier,
    checkJump: takeAtNextTier != null ? round((takeAtNextTier - currentTake) * 100) / 100 : null,
  };
}

// ── Coaching nudges (Phase 6) — per-weak-component plays with dollar impact ────────────
const NUDGE_PLAYS: Record<GrowthMetricKey, string> = {
  occupancy: 'Fill more of your book — take waitlist offers and add a peak-day shift.',
  rebooking: 'Book the next cut before each client leaves the chair — aim for one rebook per client.',
  returning: 'Turn first-timers into regulars — personal follow-up text within 3 days of a new client.',
  retail: 'Recommend one product per service — a simple "this is what I used" close.',
  rating: 'Ask happy clients for a quick review at checkout.',
};

export interface GrowthNudge { key: GrowthMetricKey; label: string; message: string; dollarPerWeek: number | null; }

/**
 * The bottom `n` active components → concrete coaching plays. Occupancy carries a $/week estimate
 * (closing the gap to target scales revenue proportionally); other components return null $ (indirect).
 * Same output feeds the barber view, the owner fleet view, and the AI narrator — one source of truth.
 */
export function growthNudges(
  result: GrowthResult,
  weeklyServiceRevenue: number,
  n: number = 2,
): GrowthNudge[] {
  const active = result.components.filter(c => c.active).sort((a, b) => a.componentScore - b.componentScore).slice(0, n);
  return active.map(c => {
    let dollarPerWeek: number | null = null;
    if (c.key === 'occupancy' && c.value > 0 && c.value < c.target) {
      // revenue scales ~linearly with occupancy: at target vs now.
      dollarPerWeek = round(weeklyServiceRevenue * ((c.target - c.value) / c.value));
    }
    return { key: c.key, label: c.label, message: NUDGE_PLAYS[c.key], dollarPerWeek };
  });
}

// ── Bridge to PPI: map Growth components onto the PPI business pillar (0-100) ───────────
/** Business pillar = weighted blend of the barber-controlled outcome metrics (occ/rebook/return/retail).
 *  Rating maps to clientExperience separately; the service writes both onto PpiScores. */
export function feedBusinessPillar(growth: GrowthResult): number {
  const biz: GrowthMetricKey[] = ['occupancy', 'rebooking', 'returning', 'retail'];
  const comps = growth.components.filter(c => biz.includes(c.key) && c.active && c.componentScore >= 0);
  if (!comps.length) return 0;
  const wSum = comps.reduce((s, c) => s + c.weight, 0) || 1;
  return round(comps.reduce((s, c) => s + c.componentScore * (c.weight / wSum), 0));
}
